// Preset Values utilities (iter 87).
// Each ProductView can carry a set of locked filter presets stored under
// `filters._presets`. The presets are configured in Edit View → Filters tab
// and, when the view is applied, they overwrite the corresponding regular
// filter values AND mark those fields as locked in the toolbar so the user
// cannot accidentally clear them.
//
// _presets shape: { [field_id]: { mode: string, value: any, value2?: any } }
//   - text field:   { mode: "contains" | "starts_with", value: "CI" }
//   - select field: { mode: "equals", value: "bath" }
//   - qty_range:    { mode: "range", value: 10, value2: 100 }
//   - date_range:   { mode: "range", value: "2026-01-01", value2: "2026-12-31", dateField: "contract_date" }

// Field configs the Presets UI knows how to render / persist. Extending this
// list is how new preset-capable filters are added.
export const PRESET_FIELDS = [
  { id: "q",             label: "Keyword",       type: "text",    modes: [{ v: "contains", label: "contains" }] },
  // iter 209 (2026-02): customer_name preset removed with the Customer filter.
  { id: "budget_no",     label: "Budget No",     type: "text",    opKey: "budget_no_op",     modes: [{ v: "contains", label: "contains" }, { v: "starts_with", label: "starts with" }] },
  { id: "factory_name",  label: "Factory",       type: "text",    opKey: "factory_name_op",  modes: [{ v: "contains", label: "contains" }, { v: "starts_with", label: "starts with" }] },
  { id: "contract_no",   label: "Contract No",   type: "text",    opKey: "contract_no_op",   modes: [{ v: "contains", label: "contains" }, { v: "starts_with", label: "starts with" }] },
  { id: "invoice_no",    label: "Invoice No",    type: "text",    opKey: "invoice_no_op",    modes: [{ v: "contains", label: "contains" }, { v: "starts_with", label: "starts with" }] },
  { id: "category",      label: "Category",      type: "select-category",     modes: [{ v: "equals", label: "equals" }] },
  { id: "product_type",  label: "Product Type",  type: "select-product-type", modes: [{ v: "equals", label: "equals" }] },
  { id: "approval",      label: "Approval",      type: "select-approval",     modes: [{ v: "equals", label: "equals" }] },
  { id: "has_samples",   label: "Has Sample Stock", type: "select-bool", modes: [{ v: "equals", label: "equals" }] },
  { id: "qty_range",     label: "Qty Range",     type: "qty_range",   modes: [{ v: "range", label: "between" }] },
  { id: "date_range",    label: "Date Range",    type: "date_range",  modes: [{ v: "range", label: "between" }] },
];

// Filter-key IDs a given preset locks on the toolbar (a preset covers one or
// more toolbar fields; e.g. a qty_range preset locks both min_qty and max_qty).
const LOCKED_KEYS_BY_PRESET = {
  q: ["q"],
  // iter 209: customer_name removed.
  budget_no: ["budget_no"],
  factory_name: ["factory_name"],
  contract_no: ["contract_no"],
  invoice_no: ["invoice_no"],
  category: ["category"],
  product_type: ["product_type"],
  approval: ["approval"],
  has_samples: ["has_samples"],
  qty_range: ["min_qty", "max_qty"],
  date_range: ["date_field", "date_from", "date_to"],
};

// Given a base (empty or view-supplied) filters object and a `_presets` map,
// return the fully-materialized filter values plus the locked-field id list.
export function applyPresetsToFilters(baseFilters, presetsMap) {
  const filters = { ...baseFilters };
  const locked = [];
  if (!presetsMap || typeof presetsMap !== "object") {
    return { filters, lockedFields: locked };
  }
  for (const [fieldId, preset] of Object.entries(presetsMap)) {
    if (!preset || preset.mode === "none" || preset.mode == null) continue;
    const cfg = PRESET_FIELDS.find((f) => f.id === fieldId);
    if (!cfg) continue;

    if (cfg.type === "text") {
      if (preset.value == null || preset.value === "") continue;
      filters[fieldId] = preset.value;
      if (cfg.opKey) filters[cfg.opKey] = preset.mode;
      locked.push(...LOCKED_KEYS_BY_PRESET[fieldId]);
    } else if (cfg.type === "select-bool") {
      if (preset.value !== "true" && preset.value !== true) continue;
      filters[fieldId] = true;
      locked.push(...LOCKED_KEYS_BY_PRESET[fieldId]);
    } else if (cfg.type.startsWith("select-")) {
      if (preset.value == null || preset.value === "") continue;
      filters[fieldId] = preset.value;
      locked.push(...LOCKED_KEYS_BY_PRESET[fieldId]);
    } else if (cfg.type === "qty_range") {
      if (preset.value === "" && preset.value2 === "") continue;
      filters.min_qty = preset.value ?? "";
      filters.max_qty = preset.value2 ?? "";
      locked.push(...LOCKED_KEYS_BY_PRESET[fieldId]);
    } else if (cfg.type === "date_range") {
      if (!preset.value && !preset.value2) continue;
      if (preset.dateField) filters.date_field = preset.dateField;
      filters.date_from = preset.value || "";
      filters.date_to = preset.value2 || "";
      locked.push(...LOCKED_KEYS_BY_PRESET[fieldId]);
    }
  }
  return { filters, lockedFields: locked };
}

export function emptyPreset(fieldId) {
  const cfg = PRESET_FIELDS.find((f) => f.id === fieldId);
  if (!cfg) return { mode: "none", value: "" };
  return { mode: cfg.modes[0].v, value: "", value2: "" };
}
